import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullseye, faGamepad, faGear, faMobileScreenButton, faChartColumn, faBookOpen, faPeopleGroup, faHandshake, faRobot, faHospital, faGraduationCap } from '@fortawesome/free-solid-svg-icons';
import './AboutPage.css';

const AboutPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

return (
  <div className="about-page">
    {/* Floating Gradient Orbs */}
    <div className="floating-orbs">
      <div className="orb orb-1"></div>
      <div className="orb orb-2"></div>
      <div className="orb orb-3"></div>
      <div className="orb orb-4"></div>
      <div className="orb orb-5"></div>
    </div>
    
    <div className="about-container">
      {/* Hero Section */}
        <motion.div 
          className="about-hero"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="about-title">
            Empowering Lives Through <span>Therapeutic Gaming</span>
          </h1>
          <p className="about-subtitle">
            enAble Games is revolutionizing rehabilitation and therapy by creating 
            customizable Active Video Games (AVG) that promote health and activity 
            in people with disabilities.
          </p>
        </motion.div>

        {/* Mission Section */}
        <motion.section 
          className="mission-section"
          {...fadeInUp}
          transition={{ delay: 0.2 }}
        >
          <div className="section-header">
            <div className="section-icon"><FontAwesomeIcon icon={faBullseye} color='#1515b6ff'/></div>
            <h2 className="section-title">Our Mission</h2>
          </div>
          
          <div className="mission-content">
            <p>
              We enable all people to play! People with disabilities have limited options for active recreation and exercise. enAble Games provides a solution by offering access to affordable active recreation and exercise for individuals with disabilities who cannot participate with their able-bodied peers.
            </p>
            <p>
              As a game design and platform development company composed of physical therapists, biomedical engineers, and computer scientists, we provide affordable, effective gaming solutions for use in clinic and home-based health and rehabilitation services.
            </p>
          </div>

          <motion.div 
            className="mission-highlights"
            variants={staggerChildren}
            initial="initial"
            animate="animate"
          >
            <motion.div className="highlight-card" variants={fadeInUp}>
              <div className="highlight-icon"><FontAwesomeIcon icon={faGamepad} color='#00009eff'/></div>
              <h3 className="highlight-title">Engaging Games</h3>
              <p className="highlight-text">
                Patients have access to challenging and entertaining AVGs that make therapy sessions more enjoyable and effective.
              </p>
            </motion.div>

            <motion.div className="highlight-card" variants={fadeInUp}>
              <div className="highlight-icon"><FontAwesomeIcon icon={faGear} color='#00009eff'/></div>
              <h3 className="highlight-title">Customizable Therapy</h3>
              <p className="highlight-text">
                Clinicians and family care providers can adjust game parameters to focus on specific therapy goals for each individual.
              </p>
            </motion.div>

            <motion.div className="highlight-card" variants={fadeInUp}>
              <div className="highlight-icon"><FontAwesomeIcon icon={faMobileScreenButton} color='#00009eff'/></div>
              <h3 className="highlight-title">Accessible Anywhere</h3>
              <p className="highlight-text">
                Flexible setup allows therapy sessions in clinics or at home and on multiple devices, making consistent rehabilitation easier and more convenient.
              </p>
            </motion.div>

            <motion.div className="highlight-card" variants={fadeInUp}>
              <div className="highlight-icon"><FontAwesomeIcon icon={faChartColumn} color='#00009eff'/></div>
              <h3 className="highlight-title">Data-Driven Insights</h3>
              <p className="highlight-text">
                Motion data tracking allows examination and monitoring of patient performance to inform rehabilitation services and outcomes.
              </p>
            </motion.div>
          </motion.div>
        </motion.section>

        {/* Story Section */}
        <motion.section 
          className="story-section"
          {...fadeInUp}
          transition={{ delay: 0.3 }}
        >
          <div className="section-header">
            <div className="section-icon"><FontAwesomeIcon icon={faBookOpen} color='#0d0d5dff'/></div>
            <h2 className="section-title">Our Story</h2>
          </div>

          <div className="story-content">
            <div className="story-text">
              <p>
                Founded in 2015 in Philadelphia, PA, enAble Games takes advantage of the expertise and resources in the region and at Drexel University where the initial game design research and clinical testing was performed.
              </p>
              <p>
                Drexel University is home to one of the top pediatric physical therapy programs and one of the top-ranked game design programs in the US, providing us with unique insights and capabilities at the intersection of healthcare and interactive technology.
              </p>
            </div>
            <div className="story-visual">
              <div className="stat-number">2015</div>
              <div className="stat-label">Innovating since</div>
              <br />
              <div className="stat-number">30+</div>
              <div className="stat-label">Years of combined expertise</div>
            </div>
          </div>
        </motion.section>

        {/* Team Section */}
        <motion.section 
          className="team-section"
          {...fadeInUp}
          transition={{ delay: 0.4 }}
        >
          <div className="section-header">
            <div className="section-icon"><FontAwesomeIcon icon={faPeopleGroup} color='#1515b6ff'/></div>
            <h2 className="section-title">Meet Our Leadership Team</h2>
          </div>

          <motion.div 
            className="team-grid"
            variants={staggerChildren}
            initial="initial"
            animate="animate"
          >
            <motion.div className="team-member" variants={fadeInUp}>
              <div className="member-avatar">
                <img 
                  src="/images/maggie.jpg" 
                  alt="Maggie O'Neil"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '👩‍⚕️';
                  }}
                />
              </div>
              <h3 className="member-name">Maggie O'Neil</h3>
              <p className="member-title">Chief Science Officer</p>
              <p className="member-credentials">PhD, PT, MPH</p>
              <p className="member-bio">
                Expert clinician and physical therapist researcher bringing evidence-based approaches to therapeutic gaming.
              </p>
            </motion.div>

            <motion.div className="team-member" variants={fadeInUp}>
              <div className="member-avatar">
                <img 
                  src="/images/paulheadsquare.jpg" 
                  alt="Paul Diefenbach"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '👨‍💻';
                  }}
                />
              </div>
              <h3 className="member-name">Paul Diefenbach</h3>
              <p className="member-title">Chief Technology Officer</p>
              <p className="member-credentials">PhD</p>
              <p className="member-bio">
                Expert game designer with 30 years of experience running digital games and simulation businesses.
              </p>
            </motion.div>
          </motion.div>
        </motion.section>

        {/* Partners Section */}
        <motion.section 
          className="partners-section"
          {...fadeInUp}
          transition={{ delay: 0.5 }}
        >
          <div className="section-header">
            <div className="section-icon"><FontAwesomeIcon icon={faHandshake} color='#1515b6ff'/></div>
            <h2 className="section-title">Clinical Collaborators</h2>
          </div>
          
          <p style={{ color: '#ffffffff', fontSize: '1.1rem', marginBottom: '2rem' }}>
            We work with clinical partners testing our games with individuals with disabilities such as Cerebral Palsy, MS, and Parkinson's.
          </p>

          <motion.div 
            className="partners-grid"
            variants={staggerChildren}
            initial="initial"
            animate="animate"
          >
            <motion.div className="partner-card" variants={fadeInUp}>
              <div className="partner-logo">
                <FontAwesomeIcon icon={faRobot} color='#1d1df9ff'/>
                {/*<img 
                  src="/images/recupero-robotics-logo.png" 
                  alt="Recupero Robotics"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '';
                  }}
                />*/}
              </div>
              <h3 className="partner-name">Recupero Robotics</h3>
              <p className="partner-description">
                Leading rehabilitation robotics research and development
              </p>
            </motion.div>

            <motion.div className="partner-card" variants={fadeInUp}>
              <div className="partner-logo">
                <FontAwesomeIcon icon={faHospital} color='#1d1df9ff'/>
                {/*<img 
                  src="/images/upenn-logo.png" 
                  alt="University of Pennsylvania"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '';
                  }}
                />*/}
              </div>
              <h3 className="partner-name">University of Pennsylvania</h3>
              <p className="partner-description">
                The Rehabilitation Robotics Lab at the School of Medicine
              </p>
            </motion.div>

            <motion.div className="partner-card" variants={fadeInUp}>
              <div className="partner-logo">
                <FontAwesomeIcon icon={faGraduationCap} color='#1d1df9ff'/>
                {/*<img 
                  src="/images/drexel-logo.png" 
                  alt="Drexel University"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '';
                  }}
                />*/}
              </div>
              <h3 className="partner-name">Drexel University</h3>
              <p className="partner-description">
                Top-ranked pediatric PT and game design programs
              </p>
            </motion.div>
          </motion.div>
        </motion.section>

        {/* CTA Section */}
        <motion.section 
          className="about-cta-section"
          {...fadeInUp}
          transition={{ delay: 0.6 }}
        >
          <h2 className="cta-title">Ready to Transform Therapy?</h2>
          <p className="cta-text">
            Join us in making rehabilitation more engaging and effective
          </p>
          <div className="cta-buttons">
            <button className="cta-btn primary">Get Started</button>
            <button className="cta-btn secondary">Contact Us</button>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default AboutPage;